import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StoryNode } from '../services/api';

interface NodeEditModalProps {
  node: StoryNode | null;
  isVisible: boolean;
  onSave: (nodeIdOrNull: string | null, updatedData: Partial<StoryNode>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  title?: string;
  modalStyle?: React.CSSProperties;
}

const NodeEditModal: React.FC<NodeEditModalProps> = ({
  node,
  isVisible,
  onSave,
  onCancel,
  isLoading = false,
  title,
  modalStyle
}) => {
  const { t } = useTranslation();
  const [editedNode, setEditedNode] = useState<Partial<StoryNode>>({});
  const [tags, setTags] = useState<string>('');

  useEffect(() => {
    if (node) {
      setEditedNode({
        label: node.label,
        title: node.title || '',
        content: node.content || ''
      });
      setTags(node.tags?.join(', ') || '');
    } else {
      setEditedNode({ label: '', title: '', content: '' });
      setTags('');
    }
  }, [node]);

  const handleSave = () => {
    const updatedData = {
      ...editedNode,
      tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
    };
    onSave(node?.id ?? null, updatedData);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        backdropFilter: 'blur(4px)',
        ...modalStyle
      }}
      onClick={onCancel}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div 
        className="modal-content"
        style={{
          background: 'linear-gradient(135deg, rgba(26, 42, 108, 0.95) 0%, rgba(58, 97, 134, 0.95) 100%)',
          border: '1px solid rgba(76, 201, 240, 0.3)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(76, 201, 240, 0.2)'
        }}>
          <h3 style={{
            margin: 0,
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 'bold'
          }}>
            <span role="img" aria-label="edit">✏️</span> {typeof title === 'string' ? title : (node ? t('nodeModal.edit.title') : t('nodeModal.create.title'))}
          </h3>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: 0.9
            }}>
              {t('nodeModal.edit.nodeTitle')} *
            </label>
            <input
              type="text"
              value={editedNode.label || ''}
              onChange={(e) => setEditedNode(prev => ({ ...prev, label: e.target.value }))}
              placeholder={t('nodeModal.edit.nodeTitle')}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(76, 201, 240, 0.3)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => e.target.style.border = '1px solid rgba(76, 201, 240, 0.6)'}
              onBlur={(e) => e.target.style.border = '1px solid rgba(76, 201, 240, 0.3)'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: 0.9
            }}>
              {t('nodeModal.edit.nodeContent')}
            </label>
            <input
              type="text"
              value={editedNode.title || ''}
              onChange={(e) => setEditedNode(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t('nodeModal.edit.nodeContent')}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(76, 201, 240, 0.3)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => e.target.style.border = '1px solid rgba(76, 201, 240, 0.6)'}
              onBlur={(e) => e.target.style.border = '1px solid rgba(76, 201, 240, 0.3)'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: 0.9
            }}>
              {t('nodeModal.edit.nodeContent')}
            </label>
            <textarea
              value={editedNode.content || ''}
              onChange={(e) => setEditedNode(prev => ({ ...prev, content: e.target.value }))}
              placeholder={t('nodeModal.edit.nodeContent')}
              disabled={isLoading}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(76, 201, 240, 0.3)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s ease',
                resize: 'vertical',
                minHeight: '80px'
              }}
              onFocus={(e) => e.target.style.border = '1px solid rgba(76, 201, 240, 0.6)'}
              onBlur={(e) => e.target.style.border = '1px solid rgba(76, 201, 240, 0.3)'}
            />
          </div>
        </div>

        <div className="modal-footer" style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid rgba(76, 201, 240, 0.2)'
        }}>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '8px',
              color: '#ffffff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.6 : 1
            }}
            onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
            onMouseLeave={(e) => !isLoading && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
          >
            {t('nodeModal.edit.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !editedNode.label?.trim()}
            style={{
              padding: '10px 20px',
              background: isLoading || !editedNode.label?.trim() 
                ? 'rgba(76, 201, 240, 0.3)' 
                : 'linear-gradient(45deg, #4361ee, #4cc9f0)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              cursor: isLoading || !editedNode.label?.trim() ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              opacity: isLoading || !editedNode.label?.trim() ? 0.6 : 1,
              boxShadow: !isLoading && editedNode.label?.trim() ? '0 2px 8px rgba(67, 97, 238, 0.3)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && editedNode.label?.trim()) {
                e.currentTarget.style.background = 'linear-gradient(45deg, #5a73f5, #5dd9f5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && editedNode.label?.trim()) {
                e.currentTarget.style.background = 'linear-gradient(45deg, #4361ee, #4cc9f0)';
              }
            }}
          >
            {isLoading ? t('nodeModal.edit.saving') : t('nodeModal.edit.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeEditModal;